import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useTheme } from "../../../contexts/ThemeContext";
import { useAuth } from "../../../contexts/AuthContext";
import { useEmpresa } from "../../../contexts/EmpresaContext";
import { temNivelMinimo } from "../../../utils/permissoes";
import { NIVEIS } from "../../../config/niveis";
import LayoutDesktop from "../../../layouts/LayoutDesktop";
import LayoutMobile from "../../../layouts/LayoutMobile";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { db } from "../../../config/firebase";
import {
  collection,
  doc,
  runTransaction,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { uploadToStorage } from "../../../utils/storageUpload";

function criarIdItemChecklist() {
  try {
    if (globalThis?.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  } catch (e) {}
  return `item_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}


const TIPO_CHECKLIST_OPCOES = [
  { value: "INSTALACAO", label: "Instalacao" },
  { value: "OS", label: "OS" },
];

async function obterProximoCodigoChecklist(grupoId) {
  const ref = doc(db, "contadores", `checklistsModelos_${grupoId}`);

  async function tx() {
    return runTransaction(db, async (transaction) => {
      const snap = await transaction.get(ref);

      let atual = 0;
      if (snap.exists() && snap.data().ultimoCodigoSequencial != null) {
        atual = snap.data().ultimoCodigoSequencial;
      }

      const novo = atual + 1;
      transaction.set(ref, { ultimoCodigoSequencial: novo, grupoId }, { merge: true });
      return novo;
    });
  }

  try {
    return await tx();
  } catch (err) {
    if (err?.code === "permission-denied") {
      try {
        await setDoc(ref, { grupoId }, { merge: true });
      } catch (e2) {
        console.warn("Falha ao reparar contador de checklists:", e2);
      }
      return await tx();
    }
    throw err;
  }
}

function NovoChecklist() {
  const { tema, alterarTema } = useTheme();
  const { usuario: usuarioLogado } = useAuth();
  const { empresaAtual } = useEmpresa() || {};
  const navigate = useNavigate();
  const isMobile = useIsMobile(900);

  const [nome, setNome] = useState("");
  const [fabricante, setFabricante] = useState("");
  const [modelo, setModelo] = useState("");
  const [tipo, setTipo] = useState("INSTALACAO");

  const [itens, setItens] = useState([]);

  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");
  const [salvando, setSalvando] = useState(false);

  const inputItemFotoCameraRefs = useRef([]);
  const inputItemFotoArquivoRefs = useRef([]);

  useEffect(() => {
    return () => {
      inputItemFotoCameraRefs.current.forEach((refEl) => {
        if (refEl?.value) {
          refEl.value = "";
        }
      });
      inputItemFotoArquivoRefs.current.forEach((refEl) => {
        if (refEl?.value) {
          refEl.value = "";
        }
      });
    };
  }, []);

  const adicionarItem = () => {
    setItens((prev) => [
      ...prev,
      {
        id: criarIdItemChecklist(),
        pergunta: "",
        tipoResposta: "SIM_NAO",
        fotoUrl: null,
        fotoFile: null,
        fotoPreview: null,
      },
    ]);
  };

  const atualizarItem = (index, campo, valor) => {
    setItens((prev) => {
      const novo = [...prev];
      novo[index] = { ...novo[index], [campo]: valor };
      return novo;
    });
  };

  const removerItem = (index) => {
    setItens((prev) => prev.filter((_, idx) => idx !== index));
  };

  const atualizarFotoItem = (index, file) => {
    setItens((prev) => {
      const novo = [...prev];
      const atual = { ...(novo[index] || {}) };

      if (atual.fotoPreview) {
        try {
          URL.revokeObjectURL(atual.fotoPreview);
        } catch (e) {}
      }

      if (file) {
        atual.fotoFile = file;
        atual.fotoPreview = URL.createObjectURL(file);
      } else {
        atual.fotoFile = null;
        atual.fotoPreview = null;
      }

      novo[index] = atual;
      return novo;
    });
  };

  const removerFotoItem = (index) => {
    setItens((prev) => {
      const novo = [...prev];
      const atual = { ...(novo[index] || {}) };

      if (atual.fotoPreview) {
        try {
          URL.revokeObjectURL(atual.fotoPreview);
        } catch (e) {}
      }

      atual.fotoFile = null;
      atual.fotoPreview = null;
      atual.fotoUrl = null;
      novo[index] = atual;
      return novo;
    });
  };

  const handleSubmit = async (evento) => {
    evento.preventDefault();
    setErro("");
    setSucesso("");

    if (!usuarioLogado?.grupoId) {
      setErro("Usuario logado nao possui grupo definido. Verifique o cadastro do Gestor.");
      return;
    }

    if (!temNivelMinimo(usuarioLogado, NIVEIS.ADMIN)) {
      setErro("Apenas Administrador/Gestor pode criar checklists.");
      return;
    }

    if (!tipo) {
      setErro("Selecione o tipo do checklist.");
      return;
    }

    if (!nome.trim()) {
      setErro("Informe o nome do checklist.");
      return;
    }

    if (!fabricante.trim()) {
      setErro("Informe o fabricante da maquina/modelo.");
      return;
    }

    if (!modelo.trim()) {
      setErro("Informe o modelo da maquina.");
      return;
    }

    try {
      setSalvando(true);
      const grupoId = usuarioLogado.grupoId;
      const empresaIdUploads =
        empresaAtual?.id || usuarioLogado?.empresaId || (usuarioLogado?.empresasPermitidas?.[0]?.id ?? null);

      const sequencial = await obterProximoCodigoChecklist(grupoId);
      const codigo = `CL${String(sequencial).padStart(4, "0")}`;

      const itensProcessados = [];
      for (let i = 0; i < itens.length; i += 1) {
        const item = itens[i] || {};
        let fotoUrl = item.fotoUrl || null;

        if (item.fotoFile) {
          if (!empresaIdUploads) {
            throw new Error("Selecione um cliente (empresa) para enviar fotos dos itens.");
          }
          fotoUrl = await uploadToStorage({
            grupoId,
            empresaId: empresaIdUploads,
            pasta: "checklists/itens",
            file: item.fotoFile,
            filenamePrefix: `${codigo}_item${String(i + 1).padStart(2, "0")}`,
          });
        }

        itensProcessados.push({
          id: item.id || criarIdItemChecklist(),
          pergunta: (item.pergunta || "").trim(),
          tipoResposta: item.tipoResposta || "SIM_NAO",
          fotoUrl: fotoUrl || null,
        });
      }

      const ref = doc(collection(db, "checklistsModelos"));
      await setDoc(ref, {
        criadoEm: serverTimestamp(),
        criadoPor: usuarioLogado.uid,
        updatedAt: serverTimestamp(),
        grupoId,
        tipo,
        codigo,
        nome: nome.trim(),
        fabricante: fabricante.trim(),
        modelo: modelo.trim(),
        itens: itensProcessados,
      });

      setSucesso("Checklist criado com sucesso!");
      setNome("");
      setFabricante("");
      setModelo("");
      setTipo("INSTALACAO");
      setItens([]);
    } catch (err) {
      console.error(err);
      setErro("Erro ao salvar checklist" + (err?.message ? `: ${err.message}` : ""));
    } finally {
      setSalvando(false);
    }
  };

  const renderFormulario = () => (
    <>
      {erro && (
        <div
          style={{
            marginTop: 12,
            padding: 8,
            borderRadius: 8,
            background: "#7f1d1d",
            color: "#fecaca",
            fontSize: 14,
          }}
        >
          {erro}
        </div>
      )}

      {sucesso && (
        <div
          style={{
            marginTop: 12,
            padding: 8,
            borderRadius: 8,
            background: "#14532d",
            color: "#bbf7d0",
            fontSize: 14,
          }}
        >
          {sucesso}
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ marginTop: 16 }}>
        <div className="pc-form-body">
          <div className="pc-form-group">
            <label>Tipo *</label>
            <select value={tipo} onChange={(e) => setTipo(e.target.value)} className="pc-input">
              {TIPO_CHECKLIST_OPCOES.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="pc-form-group">
            <label>Nome *</label>
            <input
              className="pc-input"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Ex: Checklist de instalacao esteira"
            />
          </div>

          <div className="pc-form-group">
            <label>Fabricante *</label>
            <input
              className="pc-input"
              value={fabricante}
              onChange={(e) => setFabricante(e.target.value)}
              placeholder="Ex: Movement"
            />
          </div>

          <div className="pc-form-group">
            <label>Modelo *</label>
            <input
              className="pc-input"
              value={modelo}
              onChange={(e) => setModelo(e.target.value)}
              placeholder="Ex: X100"
            />
          </div>

          <div style={{ marginTop: 24 }}>
            <h4>Itens do checklist</h4>

            {itens.length === 0 && (
              <div style={{ marginTop: 8, fontSize: 14, opacity: 0.8 }}>
                Nenhum item adicionado ainda.
              </div>
            )}

            {itens.map((item, idx) => (
              <div
                key={item.id || idx}
                style={{
                  marginTop: 12,
                  padding: 12,
                  border: "1px solid #334155",
                  borderRadius: 10,
                }}
              >
                <div className="pc-form-group">
                  <label>Pergunta *</label>
                  <input
                    className="pc-input"
                    value={item.pergunta}
                    onChange={(e) => atualizarItem(idx, "pergunta", e.target.value)}
                    placeholder="Ex: Conferir nivelamento"
                  />
                </div>

                <div className="pc-form-group">
                  <label>Tipo de resposta</label>
                  <select
                    className="pc-input"
                    value={item.tipoResposta}
                    onChange={(e) => atualizarItem(idx, "tipoResposta", e.target.value)}
                  >
                    <option value="SIM_NAO">Sim / Não</option>
                    <option value="OK_NOK">OK / NOK</option>
                    <option value="BAR">Barra / Numérico</option>
                    <option value="TEXTO">Texto Livre</option>
                  </select>
                </div>

                <div className="pc-form-group">
                  <label>Foto de referencia (opcional)</label>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button
                      type="button"
                      className="pc-btn-secundario"
                      onClick={() => inputItemFotoCameraRefs.current[idx]?.click()}
                    >
                      📷 Camera
                    </button>
                    <button
                      type="button"
                      className="pc-btn-secundario"
                      onClick={() => inputItemFotoArquivoRefs.current[idx]?.click()}
                    >
                      ⬆️ Arquivo
                    </button>
                  </div>
                  <input
                    ref={(el) => (inputItemFotoCameraRefs.current[idx] = el)}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    style={{ display: "none" }}
                    onChange={(e) => atualizarFotoItem(idx, e.target.files?.[0] || null)}
                  />
                  <input
                    ref={(el) => (inputItemFotoArquivoRefs.current[idx] = el)}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={(e) => atualizarFotoItem(idx, e.target.files?.[0] || null)}
                  />

                  {item.fotoPreview || item.fotoUrl ? (
                    <div style={{ marginTop: 8 }}>
                      <img
                        src={item.fotoPreview || item.fotoUrl}
                        alt="Preview"
                        style={{ maxWidth: 240, borderRadius: 8 }}
                      />
                      <div>
                        <button type="button" className="pc-btn-secundario" onClick={() => removerFotoItem(idx)}>
                          Remover foto
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>

                <button type="button" className="pc-btn-secundario" onClick={() => removerItem(idx)}>
                  Remover item
                </button>
              </div>
            ))}

            <button type="button" className="pc-btn-primario" onClick={adicionarItem} style={{ marginTop: 12 }}>
              + Adicionar item
            </button>
          </div>

          <div
            style={{
              marginTop: 24,
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
            }}
          >
            <button
              className="pc-btn-secundario"
              type="button"
              onClick={() => navigate("/cadastros/checklists")}
              disabled={salvando}
            >
              Cancelar
            </button>
            <button className="pc-btn-primario" type="submit" disabled={salvando}>
              {salvando ? "Salvando..." : "Salvar checklist"}
            </button>
          </div>
        </div>
      </form>
    </>
  );

  const Layout = isMobile ? LayoutMobile : LayoutDesktop;

  return (
    <Layout
      tema={tema}
      alterarTema={alterarTema}
      titulo={isMobile ? "Novo Checklist" : "NOVO CHECKLIST"}
      subtitulo={"Cadastros / Checklists / Novo"}
      abaBottom={isMobile ? "cadastros" : undefined}
    >
      {renderFormulario()}
    </Layout>
  );
}

export default NovoChecklist;
